import { UserInfoStorage } from '@/utils/userInfoStorage'
import { sha256Hex } from '@/utils/hash'

// Web equivalent of iOS's `identifierForVendor`: a random id generated once
// per browser and persisted, used to identify anonymous (signed-out) traffic.
const ANONYMOUS_DEVICE_ID_STORAGE_KEY = 'bzrt_anonymous_device_id'
const SAFETY_IDENTIFIER_HASH_LENGTH = 16

function getOrCreateAnonymousDeviceId(): string {
  try {
    const existing = localStorage.getItem(ANONYMOUS_DEVICE_ID_STORAGE_KEY)
    if (existing) {
      return existing
    }
    const generated = crypto.randomUUID()
    localStorage.setItem(ANONYMOUS_DEVICE_ID_STORAGE_KEY, generated)
    return generated
  } catch {
    // localStorage unavailable (e.g. private browsing) - fall back to a
    // session-only id rather than throwing.
    return crypto.randomUUID()
  }
}

/**
 * Builds the `safety_identifier` sent to OpenAI Responses requests, mirroring
 * the native iOS implementation (`OpenAIResponsesClient.safetyIdentifier`):
 * - Signed in: `user_<first 16 hex chars of sha256(username)>`
 * - Signed out: `device_<first 16 hex chars of sha256(anonymous device id)>`
 *
 * Never throws - returns `null` if it cannot be computed, so callers can omit
 * the field rather than fail the underlying AI request.
 */
export async function getSafetyIdentifier(): Promise<string | null> {
  try {
    const username = UserInfoStorage.get()?.username

    if (username) {
      const hash = await sha256Hex(String(username))
      return `user_${hash.slice(0, SAFETY_IDENTIFIER_HASH_LENGTH)}`
    }

    const deviceId = getOrCreateAnonymousDeviceId()
    const hash = await sha256Hex(deviceId)
    return `device_${hash.slice(0, SAFETY_IDENTIFIER_HASH_LENGTH)}`
  } catch (err) {
    console.error('Failed to compute safety identifier:', err)
    return null
  }
}
